import {signal, Signal} from "@preact/signals";
import styled from "styled-components";
import {StyledProps} from "styled-components/index";

const DEFAULT_LOADER_ID = "global-loader";

type LoaderSignals = { counter: Signal<number>, text: Signal<string>};
const signals: {[key: string]: LoaderSignals} = {};

function hasLoader(id: string) {
    return Object.prototype.hasOwnProperty.call(signals, id);
}

function getLoaderSignals(id: string): LoaderSignals | null {
    if (hasLoader(id)) {
        return signals[id];
    } else {
        return null;
    }
}

export function useLoader(id: string=DEFAULT_LOADER_ID) {
    const s = getLoaderSignals(id);
    if (!s) {
        throw new Error("Do not have loader with given ID.");
    }

    const show = (text?: string) => {
        s.counter.value += 1;

        if (text !== undefined) {
            s.text.value = text;
        }
    }

    const hide = () => {
        s.counter.value -= 1;
    }

    return [show, hide];
}

type LoaderProps = {
    text?: string;
    id?: string
} & StyledProps<HTMLDivElement>;

export const InnerLoader = styled((props: LoaderProps) => {
    const id = props.id ?? DEFAULT_LOADER_ID;

    let s: LoaderSignals;
    if (!hasLoader(id)) {
        s = signals[id] = {
            counter: signal(0),
            text: signal(props.text ?? "Loading...")
        };
    } else {
        s = getLoaderSignals(id) as LoaderSignals;
    }

    return <>{
        (s.counter.value > 0) ? <div id={props.id ?? DEFAULT_LOADER_ID} className={props.className}>{s.text.value}</div> : null
    }</>;
})`
    min-width: 15rem;
    max-width: 90%;
    background: #ecf0f1;
    border: solid 1px #95a5a6;
    box-shadow: 0.2rem 0.2rem 0.5rem rgba(0, 0, 0, 0.6);
    padding: 0.5rem;
    pointer-events: none;
    color: #2c3e50;
    text-shadow: 0.1rem 0.1rem 0.1rem rgba(0, 0, 0, 0.12);
    box-sizing: border-box;
`;

export function Loader({className, ...props}: LoaderProps) {
    return <div className={className}><InnerLoader {...props} /></div>
}

export const GlobalLoader = styled(Loader)`
    z-index: 1000;
    position: fixed;
    top: 3rem;
    left: 0;
    width: 100%;
    display: flex;
    pointer-events: none;
    justify-content: center;
`;
